/**
 * features/hr/LeaveApplications.tsx
 * ------------------------------------------------------------------
 * Per-staff leave list. Two pieces in one file:
 *
 *   LeaveApplicationsList  — pending on top, then the rest in
 *                            reverse-chronological order. Cards
 *                            (not flat rows) so the screen reads
 *                            as an application stack, not a table.
 *
 *   LeaveDetailDrawer      — slides in from the right on click,
 *                            shows the full application + the
 *                            Approve / Reject buttons (gated by
 *                            canApprove, which is derived from
 *                            permissions/role at the caller).
 *
 * For the demo, anyone with role hr or admin can approve. The
 * stricter "approver hierarchy" the school will specify tomorrow
 * can be layered in by tightening canApprove at the call site
 * without changing this file.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  Plus,
  Check,
  Ban,
  X,
  Loader2,
  CalendarDays,
  Clock,
  User as UserIcon,
  FileText,
  ChevronRight,
  Pencil,
  Printer,
  Download,
} from 'lucide-react';
import { AccentButton, SoftCard, glassFieldClass } from '@/features/finance/ui';
import { hr, syncUnpaidLeaveDeduction } from '@/features/hr/hrWrite';
import { useHrData } from '@/features/hr/useHrData';
import { usePublicHolidays } from '@/features/hr/usePublicHolidays';
import { useAuth } from '@/contexts/AuthContext';
import { countLeaveDays } from '@/lib/hrLeave';
import { currentLeaveYear, leaveBalancesFor, leaveBasis } from '@/lib/leavePolicy';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import { auditFromContext } from '@/lib/audit';
import { buildLeaveFormPdf } from '@/lib/leavePdf';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  LEAVE_TYPE_LABELS,
  staffFullName,
  type HrLeaveRequest,
  type HrStaff,
  type LeaveStatus,
  type LeaveType,
} from '@/lib/hr';
import { salesFor } from '@/lib/leaveEncashment';

const STATUS_TONE: Record<LeaveStatus, { badge: string }> = {
  pending: {
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  approved: {
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  rejected: {
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
  cancelled: {
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  },
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sameYear = s.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && s.getMonth() === e.getMonth();
  if (sameMonth) {
    return `${s.toLocaleDateString([], { day: 'numeric' })}–${e.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  return `${fmtDate(start)} – ${fmtDate(end)}`;
}
function relativeDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.round((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? '' : 's'} ago`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? '' : 's'} ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) === 1 ? '' : 's'} ago`;
}

/* ==================================================================
   LIST + EMPTY + RAISE
   ================================================================== */
interface ListProps {
  staff: HrStaff;
  applications: HrLeaveRequest[];
  /** Whether the current viewer can approve / reject. */
  canApprove: boolean;
  /** Show the in-list "New application" button. Off in the staff
   *  profile — applications are raised from the Leave nav tab. */
  allowNew?: boolean;
}

export function LeaveApplicationsList({ staff, applications, canApprove, allowNew = false }: ListProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);

  const sorted = useMemo(() => {
    const rank: Record<LeaveStatus, number> = {
      pending: 0,
      approved: 1,
      rejected: 2,
      cancelled: 3,
    };
    return [...applications].sort((a, b) => {
      if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
      return b.startDate.localeCompare(a.startDate);
    });
  }, [applications]);

  const pending = sorted.filter((a) => a.status === 'pending');
  const others = sorted.filter((a) => a.status !== 'pending');
  const opened = openId ? applications.find((a) => a.id === openId) : null;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
            Leave applications
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {applications.length} total · {pending.length} pending
          </p>
        </div>
        {allowNew && (
          <AccentButton icon={Plus} onClick={() => setRaising(true)}>
            New application
          </AccentButton>
        )}
      </div>

      {applications.length === 0 ? (
        <SoftCard hover={false}>
          <p className="text-sm text-gray-500 text-center py-6">
            No leave applications for {staff.firstName} yet.
          </p>
        </SoftCard>
      ) : (
        <>
          {pending.length > 0 && (
            <section className="space-y-2">
              <h4 className="text-[11px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-semibold">
                Awaiting decision
              </h4>
              {pending.map((a) => (
                <LeaveCard key={a.id} application={a} onClick={() => setOpenId(a.id)} />
              ))}
            </section>
          )}

          {others.length > 0 && (
            <section className="space-y-2">
              <h4 className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">
                History
              </h4>
              {others.map((a) => (
                <LeaveCard key={a.id} application={a} onClick={() => setOpenId(a.id)} muted />
              ))}
            </section>
          )}
        </>
      )}

      {opened && (
        <LeaveDetailDrawer
          application={opened}
          staff={staff}
          canApprove={canApprove}
          onClose={() => setOpenId(null)}
        />
      )}

      {allowNew && raising && (
        <RaiseLeaveModal
          staff={staff}
          onClose={() => setRaising(false)}
        />
      )}
    </div>
  );
}

/* ==================================================================
   CARD
   ================================================================== */
function LeaveCard({
  application,
  onClick,
  muted,
}: {
  application: HrLeaveRequest;
  onClick: () => void;
  muted?: boolean;
}) {
  const tone = STATUS_TONE[application.status];
  return (
    <button onClick={onClick} className="w-full text-left">
      <div
        className={`rounded-2xl bg-white dark:bg-gray-800 border ${
          application.status === 'pending'
            ? 'border-amber-200 dark:border-amber-900/40'
            : 'border-gray-100 dark:border-gray-700'
        } px-4 py-3 hover:shadow-md transition-shadow ${muted ? 'opacity-90' : ''}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--accent-from,#2d5295)] to-[var(--accent-to,#1b3a6b)] text-white flex items-center justify-center shrink-0">
            <CalendarDays size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-800 dark:text-gray-100 text-sm">
                {LEAVE_TYPE_LABELS[application.type]}
              </p>
              <span className="text-xs text-gray-400">·</span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {application.days} day{application.days === 1 ? '' : 's'}
              </p>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {fmtRange(application.startDate, application.endDate)}
            </p>
            {application.reason && (
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1.5 truncate">
                {application.reason}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-lg ${tone.badge}`}>
              {application.status}
            </span>
            <span className="text-[10px] text-gray-400">
              {application.createdAt ? `raised ${relativeDate(application.createdAt)}` : ''}
            </span>
          </div>
          <ChevronRight size={16} className="text-gray-300 shrink-0 ml-1" />
        </div>
      </div>
    </button>
  );
}

/* ==================================================================
   DETAIL DRAWER (with Approve / Reject)
   ================================================================== */
function LeaveDetailDrawer({
  application,
  staff,
  canApprove,
  onClose,
}: {
  application: HrLeaveRequest;
  staff: HrStaff;
  canApprove: boolean;
  onClose: () => void;
}) {
  const { userData } = useAuth();
  const hrData = useHrData();
  const staffLeave = hrData.leave.filter((l) => l.staffId === application.staffId);
  const storedAllocation = hrData.leaveAllocations.find((a) => a.staffId === application.staffId);
  /* The same entitlements every other leave screen uses — staff allocation
     over cohort policy over school default, type by type. Before Oct 2026
     this screen read the bare allocation, which names annual leave only, so
     sick leave was 0 here: balances showed negative and the unpaid-leave
     check below priced every sick day as unpaid. */
  const { policies } = useLeavePolicies();
  const basis = useMemo(() => leaveBasis({
    category: staff.category, allocation: storedAllocation, policies, year: currentLeaveYear(),
    accrualStart: hrData.payrollSettings?.leaveAccrualStart, employmentStart: staff.startDate,
  }), [storedAllocation, staff.category, staff.startDate, policies, hrData.payrollSettings?.leaveAccrualStart]);
  const allocation = { ...basis.allocation, staffId: application.staffId };
  const staffEncashments = salesFor(hrData.leaveEncashments, application.staffId);
  const basicSalary = hrData.payrollProfiles.find((p) => p.staffId === application.staffId)?.basicSalary ?? 0;
  const myDeductions = hrData.deductions.filter((d) => d.staffId === application.staffId);
  const [busy, setBusy] = useState<'approve' | 'reject' | 'save' | 'cancel' | null>(null);
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(application.startDate.slice(0, 10));
  const [end, setEnd] = useState(application.endDate.slice(0, 10));
  const tone = STATUS_TONE[application.status];

  const { isHoliday } = usePublicHolidays();
  /* The type decides how the span is counted: sick and hospitalisation are
     calendar days, everything else is working days. */
  const editDays = useMemo(
    () => countLeaveDays(start, end, isHoliday, application.type),
    [start, end, isHoliday, application.type],
  );

  const manageable = application.status === 'pending' || application.status === 'approved';

  async function decide(status: 'approved' | 'rejected') {
    setBusy(status === 'approved' ? 'approve' : 'reject');
    try {
      await hr.leave.update(application.id, {
        status,
        approverId: userData?.uid ?? null,
        decidedAt: new Date().toISOString(),
      });
      auditFromContext(userData, status === 'approved' ? 'leave_approved' : 'leave_rejected', 'hr', {
        targetId: application.id, targetType: 'user', targetName: application.staffId,
        details: { leaveType: application.type, days: application.days },
      });
      await syncUnpaidLeaveDeduction({
        leave: { ...application, status },
        allocation, staffLeave, basicSalary, deductions: myDeductions, staffEncashments, calc: basis.opts,
      });
      onClose();
    } finally {
      setBusy(null);
    }
  }

  async function saveDates() {
    if (editDays <= 0) return;
    setBusy('save');
    try {
      const startISO = new Date(start).toISOString();
      const endISO = new Date(end).toISOString();
      await hr.leave.update(application.id, { startDate: startISO, endDate: endISO, days: editDays });
      await syncUnpaidLeaveDeduction({
        leave: { ...application, startDate: startISO, endDate: endISO, days: editDays },
        allocation, staffLeave, basicSalary, deductions: myDeductions, staffEncashments, calc: basis.opts,
      });
      onClose();
    } finally {
      setBusy(null);
    }
  }

  async function cancelLeave() {
    setBusy('cancel');
    try {
      await hr.leave.update(application.id, {
        status: 'cancelled',
        approverId: userData?.uid ?? null,
        decidedAt: new Date().toISOString(),
      });
      auditFromContext(userData, 'leave_cancelled', 'hr', {
        targetId: application.id, targetType: 'user', targetName: application.staffId,
        details: { leaveType: application.type, days: application.days },
      });
      await syncUnpaidLeaveDeduction({
        leave: { ...application, status: 'cancelled' },
        allocation, staffLeave, basicSalary, deductions: myDeductions, staffEncashments, calc: basis.opts,
      });
      onClose();
    } finally {
      setBusy(null);
    }
  }

  const leaveForm = useMemo(() => {
    const balances = leaveBalancesFor({ category: staff.category, allocation: storedAllocation, requests: staffLeave, policies, year: currentLeaveYear(), accrualStart: hrData.payrollSettings?.leaveAccrualStart, employmentStart: staff.startDate, encashments: staffEncashments }).map((b) => ({
      label: LEAVE_TYPE_LABELS[b.type as LeaveType] ?? b.type,
      entitlement: b.entitlement,
      taken: b.taken,
      remaining: b.remaining,
    }));
    const history = staffLeave
      .filter((l) => l.id !== application.id)
      .slice()
      .sort((a, b) => b.startDate.localeCompare(a.startDate))
      .map((l) => ({
        typeLabel: LEAVE_TYPE_LABELS[l.type] ?? l.type,
        range: fmtRange(l.startDate, l.endDate),
        days: l.days,
        status: l.status,
      }));
    return { balances, history };
  }, [staffLeave, allocation, application.id]);

  async function makeLeavePdf() {
    const logo = await loadLogoDataUrl().catch(() => null);
    return buildLeaveFormPdf({
      staffName: staffFullName(staff),
      jobTitle: staff.jobTitle,
      leave: {
        typeLabel: LEAVE_TYPE_LABELS[application.type] ?? application.type,
        range: fmtRange(application.startDate, application.endDate),
        days: application.days,
        reason: application.reason,
        status: application.status,
        submitted: application.createdAt ? fmtDate(application.createdAt) : undefined,
      },
      balances: leaveForm.balances,
      history: leaveForm.history,
      date: fmtDate(new Date().toISOString()),
    }, logo);
  }
  async function printLeaveForm() {
    try { const url = (await makeLeavePdf()).output('bloburl') as unknown as string; window.open(url, '_blank'); } catch { /* ignore */ }
  }
  async function downloadLeaveForm() {
    try { (await makeLeavePdf()).save(`leave_${staffFullName(staff).replace(/\s+/g, '_')}.pdf`); } catch { /* ignore */ }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 w-full max-w-md h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">
              Leave application
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {staffFullName(staff)} · {staff.jobTitle}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center justify-between">
            <span className={`text-xs font-medium px-3 py-1.5 rounded-lg ${tone.badge}`}>
              {application.status}
            </span>
            <span className="text-xs text-gray-400">
              {application.createdAt ? `raised ${relativeDate(application.createdAt)}` : ''}
            </span>
          </div>

          <div className="space-y-1">
            <p className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
              {LEAVE_TYPE_LABELS[application.type]}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {fmtRange(application.startDate, application.endDate)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {application.days} working day{application.days === 1 ? '' : 's'}
            </p>
          </div>

          {application.reason && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1.5">
                Reason
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                {application.reason}
              </p>
            </div>
          )}

          <div className="space-y-2.5 border-t border-gray-100 dark:border-gray-700 pt-4">
            <DetailRow icon={UserIcon} label="Applicant" value={staffFullName(staff)} />
            <DetailRow icon={Clock} label="Status" value={application.status} />
            {application.decidedAt && (
              <DetailRow
                icon={Check}
                label={application.status === 'approved' ? 'Approved on' : 'Decided on'}
                value={fmtDate(application.decidedAt)}
              />
            )}
            {application.createdAt && (
              <DetailRow icon={FileText} label="Submitted" value={fmtDate(application.createdAt)} />
            )}
          </div>

          <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
            <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2.5">Approval form</p>
            <div className="flex items-center gap-2">
              <button
                onClick={printLeaveForm}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                <Printer size={14} /> Print form
              </button>
              <button
                onClick={downloadLeaveForm}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                <Download size={14} /> Download PDF
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">A5 form with leave balances and history — for the Head of School to sign before HR records approval here.</p>
          </div>

          {application.status === 'pending' && canApprove && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2.5">
                Decision
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => decide('approved')}
                  disabled={busy !== null}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {busy === 'approve' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Approve
                </button>
                <button
                  onClick={() => decide('rejected')}
                  disabled={busy !== null}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-60 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                >
                  {busy === 'reject' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                  Reject
                </button>
              </div>
            </div>
          )}

          {application.status === 'pending' && !canApprove && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <p className="text-xs text-gray-500 italic">
                Awaiting approver decision.
              </p>
            </div>
          )}

          {canApprove && manageable && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-4 space-y-3">
              <p className="text-[11px] uppercase tracking-wider text-gray-400">Manage</p>
              {!editing ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    <Pencil size={14} /> Modify dates
                  </button>
                  <button
                    onClick={cancelLeave}
                    disabled={busy !== null}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-60 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                  >
                    {busy === 'cancel' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                    Cancel leave
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Start</span>
                      <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={glassFieldClass('py-2')} />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">End</span>
                      <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className={glassFieldClass('py-2')} />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {editDays} day{editDays === 1 ? '' : 's'}
                    {editDays > application.days && <span className="text-emerald-600 dark:text-emerald-400"> · extended +{editDays - application.days}</span>}
                    {editDays < application.days && <span className="text-amber-600 dark:text-amber-400"> · cut short −{application.days - editDays}</span>}
                  </p>
                  <div className="flex items-center gap-2">
                    <AccentButton icon={busy === 'save' ? Loader2 : Check} disabled={busy !== null || editDays <= 0} onClick={saveDates}>
                      {busy === 'save' ? 'Saving…' : 'Save changes'}
                    </AccentButton>
                    <button
                      onClick={() => { setEditing(false); setStart(application.startDate.slice(0, 10)); setEnd(application.endDate.slice(0, 10)); }}
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Discard
                    </button>
                  </div>
                  {application.status === 'approved' && (
                    <p className="text-[11px] text-gray-400">Editing an approved leave keeps it approved with the new dates.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof UserIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
        <Icon size={13} /> {label}
      </span>
      <span className="text-gray-800 dark:text-gray-100">{value}</span>
    </div>
  );
}

/* ==================================================================
   RAISE LEAVE — inline modal scoped to this staff
   ================================================================== */
function RaiseLeaveModal({
  staff,
  onClose,
}: {
  staff: HrStaff;
  onClose: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const inAWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const [type, setType] = useState<LeaveType>('annual');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(inAWeek);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { isHoliday } = usePublicHolidays();
  const days = useMemo(
    () => countLeaveDays(startDate, endDate, isHoliday, type),
    [startDate, endDate, isHoliday, type],
  );

  const canSave = startDate && endDate && days > 0;

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setErr(null);
    try {
      await hr.leave.add({
        staffId: staff.id,
        type,
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        days,
        reason: reason.trim(),
        status: 'pending',
      });
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">
              New leave application
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              For {staffFullName(staff)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
              Type
            </span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as LeaveType)}
              className={glassFieldClass('py-2')}
            >
              {(Object.keys(LEAVE_TYPE_LABELS) as LeaveType[]).map((t) => (
                <option key={t} value={t}>
                  {LEAVE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
                Start
              </span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={glassFieldClass('py-2')}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
                End
              </span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={glassFieldClass('py-2')}
              />
            </label>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Duration: {days} day{days === 1 ? '' : 's'}
          </div>
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
              Reason (optional)
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className={glassFieldClass('py-2')}
            />
          </label>
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <AccentButton
            icon={saving ? Loader2 : Check}
            disabled={!canSave || saving}
            onClick={save}
          >
            {saving ? 'Submitting…' : 'Submit application'}
          </AccentButton>
        </div>
      </div>
    </div>
  );
}
